import { useEffect, useState } from 'react';
import type { PublicParty } from '@tv-casino/shared';
import { partyModel, type PartyVM } from '@tv-casino/viewmodels/components/partyModel.js';
import styles from './PartyStrip.module.css';

/** The party's view-model, re-read every second while a schedule runs down. */
function usePartyVM(party: PublicParty | null | undefined): PartyVM | null {
  const [now, setNow] = useState(() => Date.now());
  const ticking = party?.mode === 'schedule' && !party.done;
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [ticking]);
  return partyModel(party, now);
}

/**
 * Party mode on the TV: a pill in the header beside the host and seat chips,
 * so the felt keeps the height a full strip would take. The kicker already
 * names the game in play; the pill says how long it has and what follows.
 */
export function PartyPill({ party }: { party: PublicParty | null | undefined }) {
  const vm = usePartyVM(party);
  if (!vm) return null;
  return (
    <div className={[styles.pill, vm.due ? styles.pillDue : ''].filter(Boolean).join(' ')} aria-label="Party mode">
      <span className={styles.pillLabel}>{vm.pillLabel}</span>
      <span className={styles.pillValue}>{vm.pillValue}</span>
    </div>
  );
}

/**
 * Party mode as the room reads it: the lineup with the game in play lit, how
 * long (or how many rounds) it has left, and what comes next, as one card
 * on a phone. Watch-only — the host runs the party from their panel.
 */
export function PartyStrip({ party }: { party: PublicParty | null | undefined }) {
  const vm = usePartyVM(party);
  if (!vm) return null;

  return (
    <section
      className={[styles.strip, vm.due ? styles.due : ''].filter(Boolean).join(' ')}
      aria-label="Party mode"
    >
      <div className={styles.head}>
        <p className={styles.heading}>Party mode{vm.lapLine ? ` · ${vm.lapLine}` : ''}</p>
        <p className={styles.progress}>{vm.progressLine}</p>
      </div>
      <ol className={styles.steps}>
        {vm.steps.map((step) => (
          <li key={step.game} className={styles[step.state]} aria-current={step.state === 'current' || undefined}>
            {step.label}
          </li>
        ))}
      </ol>
      {(vm.nextLine || vm.endLine) && (
        <p className={styles.meta}>{[vm.nextLine, vm.endLine].filter(Boolean).join(' · ')}</p>
      )}
    </section>
  );
}
